import { useEffect, useRef, useState } from 'react';
import { cn } from '@shared/lib';
import { LANGUAGE_LABELS, type Language } from '@shared/types';

interface LanguageSelectProps {
  label: string;
  value: Language;
  onChange: (lang: Language) => void;
  options: Language[];
  /** Подмножество options, которое реально можно выбрать. Остальные показываются
      заблокированными («скоро»). Если не передан — доступны все. */
  available?: Language[];
  /** 'accent' — янтарный чип кода (для «Изучаю»), 'neutral' — тёпло-серый (для «Перевод»). */
  tone?: 'accent' | 'neutral';
}

const codeOf = (lang: Language) => lang.toUpperCase();

/**
 * Кастомный языковой селект из дизайна CreateModule.dc.html: чип с кодом языка
 * (EN/RU, не флаг), название и каретка ▾. Дропдаун — с галочкой выбранного,
 * закрывается по клику вне и по Esc (Esc гасится, чтобы не закрылась модалка).
 */
export function LanguageSelect({ label, value, onChange, options, available, tone = 'neutral' }: LanguageSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation(); // гасим на capture: закрываем только дропдаун, не модалку
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const closedChip = tone === 'accent' ? 'bg-accent text-[#1a120b]' : 'bg-[#cfcabf] text-[#1a120b]';

  return (
    <div ref={rootRef} className="relative">
      <span className="mb-[9px] block text-[12px] font-semibold uppercase tracking-[0.14em] text-[#7e7b73]">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center justify-between gap-2.5 rounded-[11px] border bg-[#1b1813] px-3.5 py-3 transition-colors',
          open ? 'border-accent/55' : 'border-white/[0.09] hover:border-accent/40',
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span className={cn('rounded-[5px] px-[5px] py-[3px] font-mono text-[12px] font-bold tracking-[0.04em]', closedChip)}>
            {codeOf(value)}
          </span>
          <span className="truncate text-[15px] text-[#f0ede6]">{LANGUAGE_LABELS[value]}</span>
        </span>
        <span className={cn('text-[12px] leading-none text-[#8a877f] transition-transform duration-200', open && 'rotate-180')}>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute inset-x-0 top-full z-30 mt-[7px] max-h-[248px] animate-cm-dropdown-in overflow-y-auto rounded-xl border border-white/10 bg-[#1c1914] p-1.5 shadow-[0_24px_50px_-16px_rgba(0,0,0,0.85)]"
        >
          {options.map((lang) => {
            const selected = lang === value;
            const locked = available ? !available.includes(lang) : false;
            return (
              <button
                key={lang}
                type="button"
                role="option"
                aria-selected={selected}
                aria-disabled={locked || undefined}
                disabled={locked}
                onClick={() => {
                  onChange(lang);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors',
                  locked
                    ? 'cursor-not-allowed opacity-40'
                    : selected
                      ? 'bg-accent/[0.12]'
                      : 'hover:bg-white/[0.05]',
                )}
              >
                <span
                  className={cn(
                    'rounded-[5px] px-[5px] py-[3px] font-mono text-[12px] font-bold text-[#1a120b]',
                    locked ? 'bg-[#6b6860]' : 'bg-[#cfcabf]',
                  )}
                >
                  {codeOf(lang)}
                </span>
                <span className="flex-1 text-[15px] text-[#e9e6df]">{LANGUAGE_LABELS[lang]}</span>
                {locked ? (
                  <span className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[#7e7b73]">скоро</span>
                ) : selected ? (
                  <span className="text-[12px] text-success">✓</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
